import { useRuntimeConfig } from "#imports";
import {
  computed,
  readonly,
  ref,
  shallowRef,
  toValue,
  type ComputedRef,
  type MaybeRefOrGetter,
  type Ref
} from "vue";
import {
  TuitionEstimateDomainError,
  buildTuitionCostUrl,
  buildTuitionEstimates,
  createTuitionRequestSignature,
  normalizeAcademicYears,
  normalizeTuitionEstimateInput,
  normalizeTuitionYearSource
} from "~/src/services/TuitionEstimateService";
import type {
  NormalizedTuitionEstimateInput,
  SemesterTotalsByYear,
  TuitionEstimateApiResponse,
  TuitionEstimateBuildResult,
  TuitionEstimateError,
  TuitionEstimateErrorCode,
  TuitionEstimateInput,
  TuitionYearEstimate,
  TuitionYearSource
} from "~/types/types";

export interface UseTuitionCostEstimateReturn {
  estimates: ComputedRef<TuitionYearEstimate[]>;
  semesterTotals: ComputedRef<SemesterTotalsByYear>;
  pending: Readonly<Ref<boolean>>;
  error: ComputedRef<TuitionEstimateError | null>;
  isStale: ComputedRef<boolean>;
  execute: () => Promise<void>;
  clearError: () => void;
}

const emptyBuildResult = (): TuitionEstimateBuildResult => ({
  estimates: [],
  semesterTotals: {}
});

const toPublicError = (
  error: unknown,
  fallbackCode: TuitionEstimateErrorCode
): TuitionEstimateError => {
  if (error instanceof TuitionEstimateDomainError) {
    return { code: error.code, message: error.message };
  }

  return {
    code: fallbackCode,
    message:
      fallbackCode === "network"
        ? "Unable to retrieve tuition cost information"
        : "Unable to calculate tuition cost information"
  };
};

export const useTuitionCostEstimate = (
  inputSource: MaybeRefOrGetter<TuitionEstimateInput>
): UseTuitionCostEstimateReturn => {
  const runtimeConfig = useRuntimeConfig();
  const sourceYears = shallowRef<TuitionYearSource[]>([]);
  const successfulRequestSignature = ref<string | null>(null);
  const pending = ref(false);
  const executionError = ref<TuitionEstimateError | null>(null);
  const executionToken = ref(0);

  const isStale = computed(() => {
    if (successfulRequestSignature.value === null) return false;

    try {
      return (
        createTuitionRequestSignature(toValue(inputSource)) !== successfulRequestSignature.value
      );
    } catch {
      return true;
    }
  });

  const calculationState = computed<{
    result: TuitionEstimateBuildResult;
    error: TuitionEstimateError | null;
  }>(() => {
    if (sourceYears.value.length === 0 || isStale.value || executionError.value !== null) {
      return { result: emptyBuildResult(), error: null };
    }

    try {
      return {
        result: buildTuitionEstimates(sourceYears.value, toValue(inputSource)),
        error: null
      };
    } catch (error) {
      return {
        result: emptyBuildResult(),
        error: toPublicError(error, "validation")
      };
    }
  });

  const estimates = computed(() => calculationState.value.result.estimates);
  const semesterTotals = computed(() => calculationState.value.result.semesterTotals);
  const error = computed(() => calculationState.value.error ?? executionError.value);

  const clearError = (): void => {
    executionError.value = null;
  };

  const execute = async (): Promise<void> => {
    const token = executionToken.value + 1;
    executionToken.value = token;
    const isCurrentExecution = () => token === executionToken.value;
    const inputSnapshot = { ...toValue(inputSource) };
    let normalizedInput: NormalizedTuitionEstimateInput;

    try {
      normalizedInput = normalizeTuitionEstimateInput(inputSnapshot);
    } catch (caughtError) {
      if (isCurrentExecution()) {
        executionError.value = toPublicError(caughtError, "validation");
        pending.value = false;
      }
      return;
    }

    if (isCurrentExecution()) {
      pending.value = true;
      executionError.value = null;
    }

    const requestSignature = createTuitionRequestSignature(inputSnapshot);
    const academicYears = normalizeAcademicYears(
      normalizedInput.costAcademicYear,
      normalizedInput.latestAcademicYear
    );

    try {
      const urls = academicYears.map((academicYear) =>
        buildTuitionCostUrl(
          String(runtimeConfig.public.asuoApiBaseUrl),
          String(runtimeConfig.public.asuoApiCostInfoPath),
          normalizedInput,
          academicYear
        )
      );
      /*   const responses = await Promise.all(
        urls.map((url) => $fetch<TuitionEstimateApiResponse>(url)),
      );*/

      const responses = await Promise.all(
        urls.map((url) =>
          ($fetch as <T>(request: string) => Promise<T>)<TuitionEstimateApiResponse>(url)
        )
      );

      const normalizedSources = responses.map((response, index) =>
        normalizeTuitionYearSource(academicYears[index], response)
      );

      buildTuitionEstimates(normalizedSources, normalizedInput);

      if (!isCurrentExecution()) return;

      sourceYears.value = normalizedSources;
      successfulRequestSignature.value = requestSignature;
      executionError.value = null;
    } catch (caughtError) {
      if (!isCurrentExecution()) return;

      executionError.value = toPublicError(caughtError, "network");
    } finally {
      if (isCurrentExecution()) {
        pending.value = false;
      }
    }
  };

  return {
    estimates,
    semesterTotals,
    pending: readonly(pending),
    error,
    isStale,
    execute,
    clearError
  };
};
